import { useState, useEffect, useRef } from 'react';

// RTK Query
import { useStartJobMutation, useGetJobQuery, JobStatus } from '@/redux/api/ai-statement-reader';

// Types
import { MultiPortfolioData } from '@/redux/modules/types/ai-statement-reader-multi-portfolio';

type UseAiStatementReaderJobProps = {
  storeMultiPortfolioResults: (portfolios: MultiPortfolioData[]) => void;
  setIsProcessingError: (isProcessingError: boolean) => void;
};

export function useAiStatementReaderJob({ storeMultiPortfolioResults, setIsProcessingError }: UseAiStatementReaderJobProps) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [shouldStopPolling, setShouldStopPolling] = useState(false);

  const [startJob, { isLoading: isStartingJob }] = useStartJobMutation();

  const { data: jobData, error: jobError } = useGetJobQuery(jobId!, {
    skip: !jobId || shouldStopPolling,
    pollingInterval: 2000,
  });

  useEffect(() => {
    if (jobData) {
      if (jobData.status === JobStatus.COMPLETED) {
        if (jobData.data?.portfolios) {
          storeMultiPortfolioResults(jobData.data.portfolios);
          setShouldStopPolling(true);
        }
      } else if (jobData.status === JobStatus.FAILED) {
        setIsProcessingError(true);
        setShouldStopPolling(true);
      }
    }
  }, [jobData, storeMultiPortfolioResults, setIsProcessingError]);

  useEffect(() => {
    if (jobError) {
      setIsProcessingError(true);
      setShouldStopPolling(true);
    }
  }, [jobError, setIsProcessingError]);

  const startJobWithFile = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await startJob(formData).unwrap();
      setJobId(result.jobId);
      return result.jobId;
    } catch (error) {
      setIsProcessingError(true);
      return null;
    }
  };

  const resetJob = () => {
    setJobId(null);
    setShouldStopPolling(false);
  };

  return {
    jobId,
    jobData,
    isStartingJob,
    startJobWithFile,
    resetJob,
  };
}
